<template>
  <div class="collections-panel">
    <div class="header">
      <div v-if="conversationScope.breadcrumb.length" class="header-breadcrumb">
        <BbBtn size="x-small" type="icon" icon="chevron-left" icon-size="small" no-background @click="goBack"></BbBtn>
        <span v-if="conversationScope.breadcrumb.length === 1">{{ t('collections.collections') }} </span>
        <span v-if="conversationScope.breadcrumb.length === 2">..</span>
        <span v-for="(crumb, index) in conversationScope.breadcrumb" :key="index">{{ '/' + crumb.title }}</span>
      </div>
      <div v-else class="header-title">
        <TextBody tag="span" i18n-key="collections.collections" weight="medium" :body-type="2"></TextBody>
        <span class="count">{{ '(' + collectionsCount + ')' }} </span>
        <BbBtn class="create-button" size="small" type="secondary" @click="createNewCollection">
          <BbIcon size="small" name="plusincircle" />
          <TextBody i18n-key="collections.newCollection"></TextBody>
        </BbBtn>
      </div>

      <BbBtn
        class="close-button"
        size="x-small"
        type="icon"
        icon="x-small"
        icon-size="medium"
        no-background
        @click="onClose"
      />
    </div>

    <CollectionsContainer
      v-show="conversationScope.breadcrumb.length === 0"
      :current-collections="currentCollections"
    />
    <CollectionDetailsContainer
      v-show="conversationScope.breadcrumb.length === 1"
      :collection-id="conversationScope.collection.collectionId"
      @navigate="goBack"
    />
    <DocumentContainer v-if="conversationScope.breadcrumb.length === 2" />
  </div>
</template>

<script>
import { onUnmounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import useCollections from '~/composables/useCollections';
import useConversations from '~/composables/useConversations';
import useModals from '~/composables/useModals';

import CollectionsContainer from '~/components/conversation/collections/CollectionsContainer';
import CollectionDetailsContainer from '~/components/conversation/collections/CollectionDetailsContainer';
import DocumentContainer from '~/components/conversation/DocumentContainer';
import TextBody from '@/components/common/TextBody.vue';

export default {
  components: { TextBody, DocumentContainer, CollectionsContainer, CollectionDetailsContainer },
  setup() {
    const { t } = useI18n();
    const { currentCollections } = useCollections();
    const { setConversationScope, conversationScope } = useConversations();
    const { setCurrentDocumentsDialog, setCreateCollectionPopup } = useModals();
    const confirmDeletePopupOpened = ref(false);

    const collectionsCount = computed(() => currentCollections.value.length);

    const createNewCollection = () => setCreateCollectionPopup({ open: true, collection: {} });

    const goBack = () => {
      if (conversationScope.value.breadcrumb.length > 1) {
        const breadcrumb = conversationScope.value.breadcrumb.slice(0, -1);
        setConversationScope({ breadcrumb });
      } else {
        const breadcrumb = [];
        setConversationScope({ isScoped: false, collection: {}, breadcrumb });
      }
    };

    const onClose = () => {
      setCurrentDocumentsDialog(null);
    };

    onUnmounted(() => setConversationScope({ isScoped: false, collection: {}, breadcrumb: [] }));

    return {
      conversationScope,
      confirmDeletePopupOpened,
      currentCollections,
      goBack,
      onClose,
      t,
      collectionsCount,
      createNewCollection,
    };
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.collections-panel {
  @include media-breakpoint-up('sm') {
    display: flex;
  }

  max-height: calc(100vh - 80px);

  // take into consideration the margins
  height: 100%;
  display: none;
  flex-direction: column;
  background-color: $bb-neutral-6;
  border-radius: $bb-spacing-8;
  border: 1px solid #cecdcb;
  box-shadow: 0 0 $bb-spacing-2 0 #0000001a;
  align-items: center;
  min-width: 348px;
  max-width: 348px;
  padding: $bb-spacing-4 $bb-spacing-4 $bb-spacing-4 $bb-spacing-4;
  overflow-x: hidden;
  overflow-y: auto;

  .header {
    display: flex;
    width: 100%;
    flex-shrink: 1; /* Allows shrinking if necessary */

    .header-title {
      .count {
        @include bb-regular-text-styles('md');
      }

      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      gap: $bb-spacing-2;
      margin-bottom: $bb-spacing-6;

      .create-button {
        :deep(.v-btn__content) {
          gap: $bb-spacing-1;
        }
      }
    }

    .header-breadcrumb {
      display: flex;
      max-width: 90%;
      align-items: center;
      flex-shrink: 1; /* Allows shrinking if necessary */

      button {
        padding: 0;
      }

      span {
        @include bb-regular-text-styles('sm');
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        max-width: 100%;
        font-weight: 500;
      }
    }

    .close-button {
      margin-left: auto; /* This pushes the third item (button) to the end of the flex container */
    }
  }
}
</style>
